// ABOUTME: Pie chart component with integrated child avatars for analytics dashboard
// ABOUTME: Shows distribution of stars/hearts by child with profile images in slices

import React, { useRef, useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, TooltipContentProps } from 'recharts';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Mic as MicIcon } from '@mui/icons-material';
import { getAvatarUrl } from '../utils/avatarUtils';
import {
  calculateResponsivePieChartRadius,
  calculateAvatarRadius,
  calculateContainerHeight,
  isSystemEntity,
  AVATAR_CONFIG,
  SYSTEM_ENTITY_CONFIG,
  calculateMobileFontSize,
  extractFirstName,
  useMobileLayout
} from '../utils/chartUtils';
import {
  PieChartDataPoint,
  ChartEntity,
  EntityType,
  ChartDataType
} from '../types/chart';

interface PieChartWithAvatarsProps {
  data: PieChartDataPoint[];
  entities: ChartEntity[];
  title: string;
  dataType: ChartDataType;
  entityType?: EntityType;
  testId?: string;
}

/**
 * Shape of the props Recharts passes into a custom Pie `label` render function.
 * Recharts' own PieLabelRenderProps type doesn't model the data-driven fields
 * (name, value, childId, id) that come from our dataKey-based dataset, so we
 * declare the subset we actually read here.
 */
interface PieAvatarLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  outerRadius: number;
  name: string;
  value: number;
  childId?: number | string;
  id?: number | string;
}

const PieChartWithAvatars: React.FC<PieChartWithAvatarsProps> = ({
  data,
  entities,
  title,
  dataType,
  testId
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(300); // Default width
  const isMobile = useMobileLayout();

  // Track container width changes
  useEffect(() => {
    const observeResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    // Initial measurement
    observeResize();

    // Set up ResizeObserver for dynamic resizing
    const resizeObserver = new ResizeObserver(observeResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate radius based on actual container width
  const outerRadius = calculateResponsivePieChartRadius(containerWidth);

  // Custom label component that shows entity avatar and count
  const renderCustomLabel = ({ cx, cy, midAngle, outerRadius: pieOuterRadius, name, value, childId, id }: PieAvatarLabelProps) => {
    const RADIAN = Math.PI / 180;

    // Calculate positioning using utility functions (container-width-aware)
    const avatarRadius = calculateAvatarRadius(pieOuterRadius);

    const avatarX = cx + avatarRadius * Math.cos(-midAngle * RADIAN);
    const avatarY = cy + avatarRadius * Math.sin(-midAngle * RADIAN);

    // Position labels vertically relative to avatar instead of around circle
    // Use avatar position as reference, place text above or below based on vertical position
    const isUpperHalf = avatarY < cy;
    const labelX = avatarX; // Center horizontally on avatar
    const labelY = isUpperHalf
      ? avatarY - 50 // Above avatar for upper half
      : avatarY + 65; // Below avatar for lower half

    // Find the entity (child or adult) by ID
    const entityId = childId || id; // Use childId for backward compatibility, id for new usage
    const entity = entities.find(e => {
      const eId = e.id; // ChartEntity always has id property
      return eId ? eId.toString() === entityId?.toString() : false;
    });

    // Check if this is a system entity using utility function
    const isSystem = isSystemEntity(entityId ?? '', entity?.isSystemCategory);

    // Get mobile-responsive values
    const displayName = isMobile ? extractFirstName(name) : name;
    const nameFontSize = isMobile ? calculateMobileFontSize() : 14;

    return (
      <g>
        {/* Avatar - larger and positioned half on/half off the slice */}
        <foreignObject x={avatarX - AVATAR_CONFIG.size / 2} y={avatarY - AVATAR_CONFIG.size / 2} width={AVATAR_CONFIG.size} height={AVATAR_CONFIG.size}>
          <Box sx={{
            width: `${AVATAR_CONFIG.size}px`,
            height: `${AVATAR_CONFIG.size}px`,
            borderRadius: '50%',
            border: `${AVATAR_CONFIG.borderWidth}px solid ${AVATAR_CONFIG.borderColor}`,
            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.grey[100],
            backgroundImage: !isSystem && getAvatarUrl(entity?.profileImageUrl) ? `url(${getAvatarUrl(entity?.profileImageUrl)})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            // fontSize matches h4, but this Box renders an SVG avatar-letter glyph, not a Typography variant
            fontSize: '20px',
            fontWeight: 600,
            color: entity?.profileImageUrl && !isSystem ? 'transparent' : theme.palette.text.secondary
          }}>
            {isSystem ? (
              // Icon glyph sizing via fontSize (24px, matching h3), not a Typography text override
              <MicIcon data-testid="mic-icon" sx={{ fontSize: `${SYSTEM_ENTITY_CONFIG.iconSize}px`, color: theme.palette.text.secondary }} />
            ) : (
              !entity?.profileImageUrl && displayName.charAt(0).toUpperCase()
            )}
          </Box>
        </foreignObject>
        {/* Label positioned above or below avatar - name on top, count below */}
        <text
          x={labelX}
          y={isUpperHalf ? labelY : labelY - 15}
          fill="black"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={nameFontSize}
          fontWeight="bold"
        >
          {displayName}
        </text>
        <text
          x={labelX}
          y={isUpperHalf ? labelY + 15 : labelY}
          fill={theme.palette.text.secondary}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={11}
        >
          {`${value} ${dataType}`}
        </text>
      </g>
    );
  };

  const renderTooltip = ({ active, payload }: TooltipContentProps<number, string>) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'white',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            padding: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {data.name}
          </Typography>
          <Typography variant="body2">
            {data.value} {dataType}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {((data.value / data.total) * 100).toFixed(1)}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Check if data is empty or all values are zero
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const isEmpty = data.length === 0 || total === 0;

  // Show empty state message if no data
  if (isEmpty) {
    const emptyMessage = dataType === 'stars' 
      ? 'No stars recorded yet. Start tracking positive behaviors!'
      : dataType === 'hearts' 
        ? 'No hearts earned yet. Help your children earn hearts by recording more stars than triangles!'
        : `No ${dataType} recorded in this time period. Start tracking to see your family's progress!`;

    return (
      <Box data-testid={testId} sx={{ height: 400, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary" sx={{ textAlign: 'center', px: 3 }}>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  const dataWithTotal = data.map(item => ({ ...item, total }));

  // Calculate responsive container height using utility function
  const containerHeight = calculateContainerHeight(outerRadius);

  return (
    <Box
      ref={containerRef}
      data-testid={testId}
      sx={{
        height: containerHeight,
        width: '100%',
        paddingLeft: isMobile ? '20px' : 0,
        paddingRight: isMobile ? '20px' : 0,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={dataWithTotal}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={(props) => renderCustomLabel(props as unknown as PieAvatarLabelProps)}
            outerRadius={outerRadius}
            fill={theme.palette.primary.main}
            dataKey="value"
          >
            {dataWithTotal.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                data-testid={`${testId}-pie-slice-${entry.name.toLowerCase()}`}
              />
            ))}
          </Pie>
          <Tooltip content={renderTooltip} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default PieChartWithAvatars;